import type { ViewMode } from '../types/lands.types'
import type { LayerType } from '../../types'

// Default layers for each mode when no previous selection exists
const DEFAULT_LAYERS = {
  plot: 'development_score' as LayerType,
  building: 'popety_classification' as LayerType,
}

const STORAGE_KEYS = {
  ACTIVE_LAYER: 'popety_active_layer',
  ACTIVE_LAYER_PLOT: 'popety_active_layer_plot',
  ACTIVE_LAYER_BUILDING: 'popety_active_layer_building',
  TRANSPARENCY: 'popety_layer_transparency',
  FAVORITE_LAYERS: 'popety_favorite_layers',
  VIEW_MODE: 'popety_view_mode',
} as const

export const layerStorage = {
  // Active Layer
  getActiveLayer: (): LayerType | null => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.ACTIVE_LAYER)
      return stored ? (stored as LayerType) : null
    } catch {
      return null
    }
  },

  setActiveLayer: (layer: LayerType | null) => {
    try {
      if (layer) {
        localStorage.setItem(STORAGE_KEYS.ACTIVE_LAYER, layer)
      } else {
        localStorage.removeItem(STORAGE_KEYS.ACTIVE_LAYER)
      }
    } catch {
      // Silent fail
    }
  },

  // Mode-specific Active Layer methods
  getActiveLayerForMode: (mode: 'plot' | 'building'): LayerType | null => {
    try {
      const key =
        mode === 'plot'
          ? STORAGE_KEYS.ACTIVE_LAYER_PLOT
          : STORAGE_KEYS.ACTIVE_LAYER_BUILDING
      const stored = localStorage.getItem(key)
      return stored ? (stored as LayerType) : DEFAULT_LAYERS[mode]
    } catch {
      return DEFAULT_LAYERS[mode]
    }
  },

  setActiveLayerForMode: (
    mode: 'plot' | 'building',
    layer: LayerType | null
  ) => {
    try {
      const key =
        mode === 'plot'
          ? STORAGE_KEYS.ACTIVE_LAYER_PLOT
          : STORAGE_KEYS.ACTIVE_LAYER_BUILDING
      if (layer) {
        localStorage.setItem(key, layer)
      } else {
        localStorage.removeItem(key)
      }
    } catch {
      // Silent fail
    }
  },

  // Transparency
  getTransparency: (): number => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.TRANSPARENCY)
      return stored ? Number(stored) : 50
    } catch {
      return 50
    }
  },

  setTransparency: (value: number) => {
    try {
      localStorage.setItem(STORAGE_KEYS.TRANSPARENCY, String(value))
    } catch {
      // Silent fail
    }
  },

  // Favorite Layers
  getFavoriteLayers: (): LayerType[] => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.FAVORITE_LAYERS)
      return stored ? JSON.parse(stored) : []
    } catch {
      return []
    }
  },

  setFavoriteLayers: (layers: LayerType[]) => {
    try {
      localStorage.setItem(STORAGE_KEYS.FAVORITE_LAYERS, JSON.stringify(layers))
    } catch {
      // Silent fail
    }
  },

  // View Mode
  getViewMode: (): ViewMode => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.VIEW_MODE)
      return stored === 'list' ? 'list' : 'map'
    } catch {
      return 'map'
    }
  },

  setViewMode: (mode: ViewMode) => {
    try {
      localStorage.setItem(STORAGE_KEYS.VIEW_MODE, mode)
    } catch {
      // Silent fail
    }
  },

  // Clear all layer-related storage
  clearAll: () => {
    try {
      Object.values(STORAGE_KEYS).forEach((key) => {
        localStorage.removeItem(key)
      })
    } catch {
      // Silent fail
    }
  },

  // Clear mode-specific layers (for migration/debugging)
  clearModeSpecificLayers: () => {
    try {
      localStorage.removeItem(STORAGE_KEYS.ACTIVE_LAYER_PLOT)
      localStorage.removeItem(STORAGE_KEYS.ACTIVE_LAYER_BUILDING)
    } catch {
      // Silent fail
    }
  },
}
